/**
 * ffmpeg.wasm lazy-loader для конвертации видео в браузер-совместимый
 * H.264/MP4 перед редактированием.
 *
 * Основной юзкейс: iPhone-видео в HEVC (.mov, video/quicktime) — Chrome на
 * Windows не умеет его декодить, MediaError code 4.  Мы конвертим в MP4
 * прямо в браузере через WebAssembly-версию ffmpeg (~30 МБ core, грузится
 * один раз за сессию).
 *
 * Используем unpkg для ffmpeg-core — это единственный корректный способ
 * подгрузить wasm+worker в Vite/prod-bundle без специальной vite-конфиги
 * для copy assets.  Если прод перестанет ходить в unpkg — заменить на
 * self-hosted копию в `/public/ffmpeg/`.
 */

import { fetchFile } from '@ffmpeg/util';
import type { FFmpeg } from '@ffmpeg/ffmpeg';

let ffmpegInstance: FFmpeg | null = null;
let loadPromise: Promise<FFmpeg> | null = null;

// Self-hosted copy в /public/ffmpeg/ — файлы отдаёт тот же origin web
// контейнера; Service Worker пропускает (cache-first для .js, для .wasm
// просто proxy-through).  Передаём АБСОЛЮТНЫЕ URL, не blob: —
// внутри Worker'а ffmpeg.wasm делает importScripts(coreURL),
// относительные пути резолвятся относительно blob-origin воркера и
// перестают находить файл.
const ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';
const BASE = `${ORIGIN}/ffmpeg`;

export async function loadFfmpeg(onProgress?: (ratio: number) => void): Promise<FFmpeg> {
  if (ffmpegInstance) return ffmpegInstance;
  if (loadPromise) return loadPromise;

  loadPromise = (async () => {
    const { FFmpeg } = await import('@ffmpeg/ffmpeg');
    const ff = new FFmpeg();
    if (onProgress) ff.on('progress', ({ progress }) => onProgress(progress));

    await ff.load({
      coreURL: `${BASE}/ffmpeg-core.js`,
      wasmURL: `${BASE}/ffmpeg-core.wasm`,
    });
    ffmpegInstance = ff;
    return ff;
  })();
  return loadPromise;
}

/**
 * Перекодирует произвольное видео в H.264/AAC MP4 (faststart) — минимальный
 * формат, который гарантированно открывается <video> в любом Chrome/Firefox/Safari.
 *
 * Скорость: ~0.5–1× длины клипа на современном ноутбуке, зависит от
 * разрешения.  Для 30сек 1080p — 15–30 сек.
 */
export async function convertToH264Mp4(
  input: File,
  onProgress?: (ratio: number) => void,
): Promise<File> {
  const ff = await loadFfmpeg();
  // progress-handler нужно привязать каждый раз, чтобы не тянуть старые
  // замыкания (ffmpeg-обьект singleton).
  if (onProgress) ff.on('progress', ({ progress }) => onProgress(progress));

  const ext = (input.name.match(/\.[^.]+$/)?.[0] ?? '.mov').toLowerCase();
  const inputName = `in${ext}`;
  const outputName = 'out.mp4';

  await ff.writeFile(inputName, await fetchFile(input));

  await ff.exec([
    '-i', inputName,
    '-c:v', 'libx264',
    '-preset', 'veryfast',
    '-crf', '23',
    '-pix_fmt', 'yuv420p',
    '-c:a', 'aac',
    '-b:a', '128k',
    '-movflags', '+faststart',
    outputName,
  ]);

  const data = await ff.readFile(outputName);
  // Убираем промежуточные файлы, чтобы виртуальная ФС ffmpeg не разрасталась
  // за сессию.
  await ff.deleteFile(inputName).catch(() => {});
  await ff.deleteFile(outputName).catch(() => {});

  // @types/node vs DOM: Uint8Array<ArrayBufferLike> и Blob несовместимы в
  // строгом режиме.  Копируем в свежий ArrayBuffer, который точно подходит.
  const bytes = data instanceof Uint8Array ? data : new TextEncoder().encode(String(data));
  const ab = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  const blob = new Blob([ab], { type: 'video/mp4' });
  const newName = input.name.replace(/\.[^.]+$/, '') + '-h264.mp4';
  return new File([blob], newName, { type: 'video/mp4' });
}
